import { Injectable } from "@angular/core";
import { StoreService } from "..";
import { BehaviorSubject, catchError, from, map, Observable, of, shareReplay, switchMap, tap } from "rxjs";
import { SessionTemplateApiService } from "../../services/api/session-template-api.service";
import { compareSessionTemplates, SessionTemplate, SessionTemplateBase, SessionTemplateDetails } from "../../models/sessions/session-template.model";
import { ImageAssetUpload } from "../../models/media/cover.model";

@Injectable({
  providedIn: 'root'
})
export class SessionTemplateService {

  private detailsCache = new Map<number, Observable<SessionTemplateDetails>>();
  // The shared store's `loading` flag is flipped by every feature's load, so it cannot say
  // whether the templates in particular have arrived. This can — the same way the building
  // block templates do it.
  private readonly templatesLoadedSubject = new BehaviorSubject(false);

  constructor(
    private store: StoreService,
    private sessionTemplateApiService: SessionTemplateApiService
  ) { }

  /** True once the templates have been fetched, even when there turned out to be none. */
  get templatesLoaded$(): Observable<boolean> {
    return this.templatesLoadedSubject.asObservable();
  }

  /** Every template, in the admin's listing order (weight, then title); see `compareSessionTemplates`. */
  get sessionTemplates$(): Observable<SessionTemplate[]> {
    return this.store.state$.pipe(map(state => Object.values(state.sessionTemplates).sort(compareSessionTemplates)));
  }

  load(): Observable<SessionTemplate[]> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return this.sessionTemplateApiService.getAll().pipe(
      tap((templates) => {
        const state = { ...this.store.state };
        templates.forEach((t) => state.sessionTemplates[t.id] = t);
        state.loading = false;
        this.store.setState(state);
        this.templatesLoadedSubject.next(true);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading building session templates:', error);
        throw error;
      })
    );
  }

  loadDetails(id: number): Observable<SessionTemplateDetails> {

    const existingTemplate = this.store.state.sessionTemplates[id];
    const isDirty = existingTemplate ? !!existingTemplate.isDirty : false;

    if (!isDirty) {
      if (existingTemplate && 'isDetailed' in existingTemplate) {
        return of(existingTemplate as SessionTemplateDetails);
      }
      else if (this.detailsCache.has(id)) {
        return this.detailsCache.get(id)!;
      }
    }

    this.store.setState({ ...this.store.state, loading: true, error: null });

    const request$ = from(this.sessionTemplateApiService.get(id)).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      tap((sessionTemplate) => {
        const state = { ...this.store.state };
        sessionTemplate.isDirty = false;
        state.sessionTemplates[sessionTemplate.id] = sessionTemplate;
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.detailsCache.delete(id);
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading session template details:', error);
        throw error;
      })
    );

    this.detailsCache.set(id, request$);
    return request$;
  }

  addSessionTemplate(sessionTemplate: SessionTemplateBase, languageId: number, coverUpload?: ImageAssetUpload): Observable<SessionTemplateDetails> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.sessionTemplateApiService.add(sessionTemplate, languageId, coverUpload)).pipe(
      map((newTemplateId: number) => {
        console.debug('New session template added with ID:', newTemplateId);
        return newTemplateId;
      }),
      switchMap((newTemplateId: number) => this.loadDetails(newTemplateId)),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error adding session template:', error);
        throw error;
      })
    );
  }

  updateSessionTemplateStoreStatus(id: number, storeStatus: string): Observable<SessionTemplateDetails> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.sessionTemplateApiService.updateStoreStatus(id, storeStatus)).pipe(
      tap(() => { this.markAsDirty(id); }),
      switchMap(() => this.loadDetails(id)),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error updating session template store status:', error);
        throw error;
      })
    )
  }

  updateSessionTemplate(sessionTemplate: SessionTemplate, coverUpload?: ImageAssetUpload): Observable<SessionTemplateDetails> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.sessionTemplateApiService.update(sessionTemplate, coverUpload)).pipe(
      tap(() => { this.markAsDirty(sessionTemplate.id); }),
      switchMap(() => this.loadDetails(sessionTemplate.id)),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error updating session template:', error);
        throw error;
      })
    );
  }

  copySessionTemplate(id: number): Observable<SessionTemplateDetails> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.sessionTemplateApiService.copy(id)).pipe(
      switchMap((newTemplateId: number) => this.loadDetails(newTemplateId)),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error copying session template:', error);
        throw error;
      })
    );
  }

  private markAsDirty(id: number) {
    const state = { ...this.store.state };
    const sessionTemplate = state.sessionTemplates[id];
    if (sessionTemplate) {
      sessionTemplate['isDirty'] = true;
      this.store.setState(state);
    }
  }

  deleteSessionTemplate(id: number): Observable<void> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.sessionTemplateApiService.delete(id)).pipe(
      tap(() => {
        const state = { ...this.store.state };
        delete state.sessionTemplates[id];
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error deleting session template:', error);
        throw error;
      })
    );
  }

  clear(): void {
    const state = { ...this.store.state };
    state.sessionTemplates = {};
    this.store.setState(state);
    // Cleared on sign-out, so the next sign-in shows the templates as loading again rather
    // than as an empty list until they arrive.
    this.templatesLoadedSubject.next(false);
  }
}